import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerEventDetail } from '@/api/routes/partners/partners.events.api';
import Button from '@/components/Button';
import { IconArrowLeft } from '@/components/icon/tabler/IconArrowLeft';
import { IconBan } from '@/components/icon/tabler/IconBan';
import { IconEdit } from '@/components/icon/tabler/IconEdit';
import { IconSend } from '@/components/icon/tabler/IconSend';
import { IconTrash } from '@/components/icon/tabler/IconTrash';
import EventStatusChip from '@/domain/events/components/EventStatusChip';
import { type PartnerEventActions } from '@/domain/events/types';
import { useTranslate } from '@/state/i18n';

interface Props {
  event: PartnerEventDetail;
  actions: PartnerEventActions;
  submitting: boolean;
  onBack: () => void;
  onEdit: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

/** P10–P13 header: back, name, status and the actions the status allows. */
const EventDetailHeader = ({
  event,
  actions,
  submitting,
  onBack,
  onEdit,
  onSubmit,
  onCancel,
  onDelete,
}: Props) => {
  const t = useTranslate('domain.events.components.EventDetailHeader');
  const showSubmit = actions.submit || actions.submitBlockedBy !== null;
  const hasActions = showSubmit || actions.edit || actions.cancel || actions.remove;

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton aria-label={t('back')} onClick={onBack} size="small">
          <IconArrowLeft fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {t('backLabel')}
        </Typography>
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" minWidth={0}>
          <Typography component="h1" variant="h2" sx={{ wordBreak: 'break-word' }}>
            {event.name}
          </Typography>
          <EventStatusChip event={event} />
        </Stack>
        {hasActions ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {actions.remove ? (
              <Button variant="text" color="error" startIcon={<IconTrash />} onClick={onDelete}>
                {t('delete')}
              </Button>
            ) : null}
            {actions.cancel ? (
              <Button variant="outlined" color="error" startIcon={<IconBan />} onClick={onCancel}>
                {t('cancel')}
              </Button>
            ) : null}
            {actions.edit ? (
              <Button
                variant={showSubmit ? 'outlined' : 'contained'}
                startIcon={<IconEdit />}
                onClick={onEdit}
              >
                {event.reviewStatus === 'rejected' ? t('editAndResubmit') : t('edit')}
              </Button>
            ) : null}
            {showSubmit ? (
              <Button
                variant="contained"
                startIcon={<IconSend />}
                onClick={onSubmit}
                disabled={!actions.submit}
                loading={submitting}
              >
                {t('submit')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default EventDetailHeader;
